import React, { useState, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Wallet, Gem } from 'lucide-react'
import useAppStore from '../../../store/useAppStore'
import { getAvailableWithdrawMethods, WITHDRAW_METHODS } from '../constants/withdrawMethods'
import WithdrawMethodSelector from '../components/WithdrawMethodSelector'
import SteamTopup from '../components/SteamTopup'
import UzsWithdraw from '../components/UzsWithdraw'
import CryptoWithdraw from '../components/CryptoWithdraw'
import StarsWithdraw from '../components/StarsWithdraw'
import RubWithdraw from '../components/RubWithdraw'
import useRubWithdrawStore from '../store/useRubWithdrawStore'
import TermsAndInfo from '../../../components/TermsAndInfo'

/**
 * Withdraw Page
 * Главная страница вывода кристаллов
 * Контейнер для выбора метода вывода и отображения соответствующей формы
 */
const Withdraw = () => {
  const { t } = useTranslation()
  const { user, balance } = useAppStore()
  const rubEnabled = user?.rub_withdraw_enabled === true
  const rubMethods = useRubWithdrawStore((state) => state.info?.methods)
  const loadRubData = useRubWithdrawStore((state) => state.loadData)
  const stopRubPolling = useRubWithdrawStore((state) => state.stopPolling)

  // Экран вывода в рублях общий для СБП и карты РФ: грузим его сразу, чтобы скрыть выключенные способы
  useEffect(() => {
    if (!rubEnabled) return undefined
    loadRubData().catch(() => {})
    return () => stopRubPolling()
  }, [rubEnabled, loadRubData, stopRubPolling])

  // Доступные методы вывода на основе флагов из профиля пользователя
  // (способ в рублях с methods.*.enabled === false не показываем)
  const availableMethods = useMemo(() => {
    return getAvailableWithdrawMethods(user)
      .filter((method) => !method.rubMethod || rubMethods?.[method.rubMethod]?.enabled !== false)
  }, [user, rubMethods])

  // Выбранный метод: по умолчанию первый доступный (метод может быть недоступен — Steam/UZS)
  const [selectedMethod, setSelectedMethod] = useState(
    availableMethods[0]?.id || WITHDRAW_METHODS.STEAM
  )

  const activeMethod = availableMethods.some((method) => method.id === selectedMethod)
    ? selectedMethod
    : availableMethods[0]?.id

  // Если доступных методов нет — показываем заглушку
  if (availableMethods.length === 0) {
    return (
      <div className="text-white lg:max-w-4xl lg:mx-auto">
        <div className="p-6">
          <div className="text-center py-16">
            <Wallet size={48} className="mx-auto text-text-secondary mb-4 opacity-50" />
            <p className="text-text-secondary">{t('withdraw.noMethods', 'Вывод временно недоступен')}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="text-white lg:max-w-4xl lg:mx-auto">
      <div className="p-6">
        {/* Заголовок */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-accent-primary to-accent-secondary rounded-full flex items-center justify-center">
            <Wallet size={32} className="text-dark-primary" />
          </div>
          <h1 className="text-3xl font-bold text-accent-primary mb-2">
            {t('withdraw.pageTitle', 'Вывод кристаллов')}
          </h1>
          {/* Текущий баланс */}
          <div className="inline-flex items-center gap-2 bg-surface border border-white border-opacity-10 rounded-lg px-4 py-2">
            <span className="text-sm text-text-secondary">{t('withdraw.balance', 'Баланс')}:</span>
            <span className="font-bold text-accent-primary inline-flex items-center gap-1"><bdi dir="ltr">{balance.toLocaleString('en-US')}</bdi> <Gem size={16} className="text-accent-primary" /></span>
          </div>
        </div>

        {/* Селектор методов вывода */}
        <WithdrawMethodSelector
          selectedMethod={activeMethod}
          availableMethods={availableMethods}
          onSelect={setSelectedMethod}
        />

        {/* Контент метода вывода */}
        <div>
          {activeMethod === WITHDRAW_METHODS.STEAM && (
            <SteamTopup />
          )}
          {activeMethod === WITHDRAW_METHODS.UZS && (
            <UzsWithdraw />
          )}
          {activeMethod === WITHDRAW_METHODS.TJS && (
            <UzsWithdraw currency="tjs" />
          )}
          {activeMethod === WITHDRAW_METHODS.RUB_SBP && (
            <RubWithdraw key="sbp" method="sbp" />
          )}
          {activeMethod === WITHDRAW_METHODS.RUB_CARD && (
            <RubWithdraw key="card" method="card" />
          )}
          {activeMethod === WITHDRAW_METHODS.CRYPTO && (
            <CryptoWithdraw />
          )}
          {activeMethod === WITHDRAW_METHODS.STARS && (
            <StarsWithdraw />
          )}
        </div>
      </div>

      {/* Правила и информация */}
      <TermsAndInfo />
    </div>
  )
}

export default Withdraw
